import { useQuery } from "@tanstack/react-query";
import type { StoreListResponse } from "@/lib/api/types";
import { endpoints } from "@/lib/api/endpoints";
import { apiJsonClient } from "@/lib/query/core/api-json-client";
import { queryKeys } from "@/lib/query/core/query-keys";

// The store picker loads options client-side and SearchSelect filters them
// as the admin types. The admin stores endpoint caps `limit` at 100 and has no
// free-text search, so v1 loads the first 100 stores. `total` lets the caller
// warn when there are more stores than were loaded (extend with a server-side
// searchable Combobox when a deployment outgrows this).
const STORE_OPTIONS_LIMIT = 100;

export type StoreOption = {
  value: string;
  label: string;
  storeSlug: string;
  deactivated: boolean;
};

export function useStoreOptionsQuery(enabled: boolean) {
  const query = useQuery({
    // `status=all`: a flag can target a deactivated store too (it may be
    // reactivated); its label says it is deactivated.
    queryKey: queryKeys.admin.stores({ limit: STORE_OPTIONS_LIMIT, status: "all" }),
    queryFn: () =>
      apiJsonClient<StoreListResponse>(
        `${endpoints.admin.stores}?limit=${STORE_OPTIONS_LIMIT}&status=all`,
      ),
    enabled,
  });

  const options: StoreOption[] = (query.data?.items ?? []).map((store) => ({
    value: store.id,
    label: store.storeName,
    storeSlug: store.storeSlug,
    deactivated: Boolean(store.deactivatedAt),
  }));

  return {
    options,
    total: query.data?.total ?? 0,
    loadedCount: options.length,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}
